/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  TableBody,
  Paper,
  Typography,
} from '@mui/material';
import {
  FormationsTertiaryButton,
  FormationsSecondaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import React, { useEffect, useState } from 'react';
import { 
  AttachFileIcon, 
  DeleteIcon as DeleteOutlineIcon,
  FileUploadIcon
} from 'components/common/Icon';
import { YesNoModal } from 'components/common/modals';
import { FormationsDocument } from 'hooks/dataFormatters/useDocumentsTableData';
import { getStatus } from './statusUtil';

interface TableviewProps {
  isLoading: boolean;
  documents: FormationsDocument[];
  onComplete?: () => void;
  onDelete: (file: any) => void;
  hasCompleteCTA?: boolean;
  onFileClick?: (file: FormationsDocument) => void;
  onUpload: () => void;
  allowFileDelete?: boolean;
  showUploadFile?: boolean;
  isCompleted?: boolean;
  allowStepReject: boolean;
  onReject: () => void;
}

export const TableView = ({
  isLoading,
  documents = [],
  onDelete,
  hasCompleteCTA,
  onFileClick,
  onUpload,
  onComplete,
  allowFileDelete,
  showUploadFile,
  isCompleted = false,
  allowStepReject,
  onReject,
}: TableviewProps) => {
  const [deleteFile, setDeleteFile] = useState<any>();
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState(false);

  useEffect(() => {
    if (deleteFile) {
      setIsDeleteModelOpen(true);
    } else {
      setIsDeleteModelOpen(false);
    }
  }, [deleteFile]);

  const handleConfirmDelete = () => {
    onDelete(deleteFile);
    setDeleteFile(undefined);
  };

  return (
    <>
      <Box my={2}>
        <TableContainer component={Paper}>
          <Table aria-label="documents table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ p: 0 }}>Name</TableCell>
                <TableCell sx={{ p: 0 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc: any) => (
                <TableRow key={doc.id}>
                  <TableCell
                    scope="row"
                    sx={{ p: 0, cursor: onFileClick ? 'pointer' : 'default' }}
                    onClick={() => onFileClick?.(doc)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AttachFileIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      {doc.title}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0 }}>{getStatus(doc.status)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      visibility: allowFileDelete ? 'visible' : 'hidden',
                    }}
                  >
                    <FormationsGhostButton
                      rounded
                      aria-label="delete"
                      onClick={() => setDeleteFile(doc)}
                      disabled={isLoading || !allowFileDelete}
                    >
                      <DeleteOutlineIcon />
                    </FormationsGhostButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {documents && documents.length === 0 && (
            <Grid item sx={{ mt: 2, mb: 2 }}>
              <Typography>No documents have been uploaded yet.</Typography>
            </Grid>
          )}
        </TableContainer>
      </Box>
      {showUploadFile && (
        <Grid container item sx={{ mt: 2 }}>
          <Grid item xs={5}>
            <FormationsSecondaryButton
              size="large"
              startIcon={<FileUploadIcon sx={{ pb: 0.25 }} />}
              disabled={isLoading}
              onClick={onUpload}
            >
              Upload Document
            </FormationsSecondaryButton>
          </Grid>
        </Grid>
      )}

      <Grid
        container
        direction="row"
        justifyContent="flex-end"
        alignItems="flex-end"
        item
        xs={12}
        sx={{ mt: 2 }}
        columnSpacing={2}
      >
        {allowStepReject && !isCompleted && (
          <Grid item>
            <FormationsTertiaryButton
              isLoading={isLoading}
              size="large"
              onClick={onReject}
              data-testid="request-action-btn"
            >
              Request customer action
            </FormationsTertiaryButton>
          </Grid>
        )}
        {hasCompleteCTA && documents.length > 0 && (
          <Grid item>
            <FormationsTertiaryButton
              isLoading={isLoading}
              size="large"
              onClick={onComplete}
              disabled={isCompleted}
              data-testid="btn-complete-step"
            >
              {isCompleted ? 'Completed' : 'Complete'}
            </FormationsTertiaryButton>
          </Grid>
        )}
      </Grid>

      <YesNoModal
        open={isDeleteModelOpen}
        heading="Are you sure you want to delete this file?"
        okText="Delete"
        okButtonStyle="error"
        onSave={handleConfirmDelete}
        onClose={() => setDeleteFile(undefined)}
      />
    </>
  );
};

TableView.defaultProps = {
  hasCompleteCTA: true,
  allowFileDelete: true,
  showUploadFile: true,
};
